import { useState, useMemo } from 'react'
import { useNews } from '../../hooks/useNews'
import { useAppConfig } from '../../hooks/useAppConfig'
import LoadingSpinner from '../ui/LoadingSpinner'

const TAB_DEFS = {
  nba: { key: 'nba', label: 'NBA' },
  nfl: { key: 'nfl', label: 'NFL' },
  mlb: { key: 'mlb', label: 'MLB' },
  nhl: { key: 'nhl', label: 'NHL' },
  ncaaf: { key: 'ncaaf', label: 'NCAAF' },
  wnba: { key: 'wnba', label: 'WNBA' },
}
const FALLBACK_ORDER = ['nba', 'nfl', 'mlb', 'nhl', 'ncaaf', 'wnba']

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

export default function NewsFeed({ compact, label }) {
  const { data: cfg } = useAppConfig()
  const sportTabs = useMemo(() => {
    const stored = Array.isArray(cfg?.news_tab_order) && cfg.news_tab_order.length
      ? cfg.news_tab_order
      : FALLBACK_ORDER
    // Append any tab the code knows about that the stored order predates.
    // Without this, a sport added in code never appears until an admin
    // re-saves the order — the stored list is authoritative and unknown keys
    // are filtered out below. AppConfigPanel already does exactly this for
    // the leaderboard order, for the same reason; news never got it.
    // The admin's chosen ORDER still wins for everything it lists.
    const order = [...stored, ...Object.keys(TAB_DEFS).filter((k) => !stored.includes(k))]
    return order.map((k) => TAB_DEFS[k]).filter(Boolean)
  }, [cfg?.news_tab_order])

  // Track user's explicit selection separately from the effective sport.
  // useState only reads its initializer on first render, when cfg is
  // still loading — so hardcoding the initial value made the tab lock
  // to whatever the pre-config fallback picked (NBA), even after the
  // admin-configured news_tab_order arrived. Deriving effective sport
  // from state + current tab list respects both: a user's explicit
  // pick sticks, otherwise we always fall through to the leftmost tab.
  const [selectedSport, setSelectedSport] = useState(null)
  const sport = selectedSport || sportTabs[0]?.key || 'nba'
  const { data, isLoading } = useNews(sport)
  const articles = data?.articles || []

  return (
    <div>
      {/* Sport tabs, optionally prefixed by a section label so this row can
          match the feed's — "News" flush left, then the tabs — rather than
          sitting under a heading of its own. */}
      <div className="flex items-center gap-2 mb-3">
        {label && (
          <h2 className="text-xs text-text-muted uppercase tracking-wider flex-shrink-0">{label}</h2>
        )}
        {/* Scrolls on its own, and min-w-0 so it shrinks rather than pushing
            the label off the left edge. Without overflow handling here the
            row just overflows its parent, and reaching the last tab dragged
            the whole article column sideways with it. */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide min-w-0 -mx-1 px-1">
          {sportTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSelectedSport(tab.key)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                sport === tab.key ? 'bg-bg-primary/50 border-accent text-accent' : 'bg-bg-primary/50 border-text-primary/20 text-text-secondary hover:border-text-primary/40'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : !articles.length ? (
        <div className="text-center py-6 text-sm text-text-muted">No news available.</div>
      ) : (
        <div className="space-y-2">
          {articles.map((article) => (
            <a
              key={article.id}
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex gap-3 p-3 rounded-xl bg-bg-primary border border-text-primary/20 hover:bg-text-primary/5 transition-colors"
            >
              {article.image && !compact && (
                <img
                  src={article.image}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover bg-bg-secondary shrink-0"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text-primary line-clamp-2 leading-snug">
                  {article.headline}
                </div>
                {article.description && !compact && (
                  <div className="text-xs text-text-muted mt-1 line-clamp-2">{article.description}</div>
                )}
                <div className="text-[10px] text-text-muted mt-1">
                  {timeAgo(article.published)}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
